<template>
  <v-navigation-drawer
  :value="value"
  right
  temporary
  fixed
  stateless
  hide-overlay
  :width="$vuetify.breakpoint.mdAndDown ? '100vw' : '24vw'"
  :style="{ zIndex: 2600 }"
  class="nature-serve-slide-over"
  @input="$emit('input', $event)">
    <div class="ns-shell">
      <div v-if="data" class="ns-header px-5 pt-4 pb-5" :style="{ background: header.gradient }">
        <v-icon class="ns-header-watermark">{{ header.icon }}</v-icon>
        <div class="d-flex align-center">
          <div class="ns-header-badge mr-3" :style="{ borderColor: header.accent }">
            <v-icon :color="header.accent" size="22">{{ header.icon }}</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="ns-eyebrow" :style="{ color: header.accent }">{{ $t('natureServe') }}</div>
            <div v-if="headerPrimary" class="ns-common-name">{{ headerPrimary }}</div>
            <div v-if="headerSecondary" class="ns-sci-name">{{ headerSecondary }}</div>
          </div>
          <BaseDialogActions hideRefresh hideFullscreen customCancel @close="$emit('input', false)"/>
        </div>
      </div>

      <div class="ns-body" @wheel.stop>
        <div v-if="loading" class="text-center py-16 px-6">
          <v-progress-circular indeterminate color="#2C5E40" size="38"/>
          <p class="mt-4 mb-0 ns-muted">{{ $t('queryingNatureServe') }}</p>
        </div>

        <div v-else-if="error" class="text-center py-16 px-6">
          <v-icon large color="#9AA79D">mdi-alert-circle-outline</v-icon>
          <p class="mt-4 mb-0 ns-muted">{{ errorMessage }}</p>
        </div>

        <div v-else-if="!data" class="text-center py-16 px-6">
          <v-icon large color="#9AA79D">mdi-database-off-outline</v-icon>
          <p class="mt-4 mb-4 ns-muted">{{ $t('natureServeNoMatch') }}</p>
          <v-btn small outlined color="primary" @click="openExplorerHome">
            <v-icon small left>mdi-open-in-new</v-icon>
            {{ $t('searchNatureServeExplorer') }}
          </v-btn>
        </div>

        <div v-else class="pa-5">
          <div v-if="isClosestMatch" class="ns-approx mb-4">
            <div class="ns-section-label ns-approx-label">
              <v-icon size="14" color="#8A5A1E" class="mr-1">mdi-help-circle-outline</v-icon>
              {{ $t('closestMatch') }}
            </div>
            <div class="ns-prose">{{ closestMatchNotice }}</div>
          </div>

          <a
          v-if="data.elementId"
          :href="explorerUrl"
          target="_blank"
          rel="noopener"
          class="ns-explorer-link mb-4"
          @click.stop.prevent="openExplorer">
            {{ explorerLabel }}
            <v-icon size="14" color="#2C5E40" class="ml-1">mdi-open-in-new</v-icon>
          </a>

          <div class="mb-6">
            <div class="ns-rank-grid">
              <div
              v-for="rank in rankTiles"
              :key="rank.label"
              class="ns-rank-tile">
                <div class="ns-tile-label">{{ rank.label }}</div>
                <div class="ns-tile-value" :class="{ 'ns-tile-empty': !rank.value }">
                  {{ rank.value || '—' }}
                </div>
              </div>
            </div>
            <div v-if="stateRankNotMatched" class="text-caption ns-muted mt-2">
              {{ $t('stateRankNotMatched', { state: stateCode }) }}
            </div>
          </div>

          <div v-if="data.majorHabitat" class="mb-5">
            <div class="ns-section-label">
              <v-icon size="14" color="#2C5E40" class="mr-1">mdi-terrain</v-icon>
              {{ $t('majorHabitat') }}
            </div>
            <div class="ns-prose">{{ data.majorHabitat }}</div>
          </div>

          <div v-if="data.habitatGroups && data.habitatGroups.length" class="mb-5">
            <div class="ns-section-label">
              <v-icon size="14" color="#2C5E40" class="mr-1">mdi-pine-tree</v-icon>
              {{ $t('habitatTypes') }}
            </div>
            <div
            v-for="group in data.habitatGroups"
            :key="group.realm"
            class="ns-habitat-group">
              <div class="ns-habitat-realm">{{ realmLabel(group.realm) }}</div>
              <div class="ns-habitat-chips">
                <v-chip
                v-for="type in group.types"
                :key="type"
                x-small
                label
                outlined
                class="ns-habitat-chip"
                :class="{ 'ns-habitat-chip--actionable': canCreateHabitat(group.realm, type) }"
                @click="canCreateHabitat(group.realm, type) && startHabitatFromType(group.realm, type)">
                  {{ type }}
                  <v-icon v-if="canCreateHabitat(group.realm, type)" x-small right class="ns-habitat-chip-add">mdi-plus-circle-outline</v-icon>
                </v-chip>
              </div>
            </div>
          </div>

          <div v-if="data.habitatComments" class="mb-5">
            <div class="ns-section-label">
              <v-icon size="14" color="#2C5E40" class="mr-1">mdi-text-long</v-icon>
              {{ $t('habitatComments') }}
            </div>
            <div class="ns-prose">{{ data.habitatComments }}</div>
          </div>

          <div v-if="data.keyThreats || data.degreeOfThreat" class="ns-threats mb-5">
            <div class="ns-section-label ns-threats-label">
              <v-icon size="14" color="#B14A17" class="mr-1">mdi-alert-outline</v-icon>
              {{ $t('keyThreats') }}
            </div>
            <div v-if="data.degreeOfThreat" class="ns-degree-of-threat font-weight-bold">
              {{ $t('degreeOfThreat') }}: {{ data.degreeOfThreat }}
            </div>
            <div v-if="data.keyThreats" class="ns-prose">{{ data.keyThreats }}</div>
          </div>

          <v-divider class="mb-3"/>
          <div v-if="recordDateLine" class="text-caption ns-muted mb-2">{{ recordDateLine }}</div>
          <i18n path="natureServeCitation" tag="div" class="ns-citation">
            <template #year>{{ citationYear }}</template>
            <template #date>{{ citationDate }}</template>
            <template #link>
              <a
              href="https://explorer.natureserve.org/"
              target="_blank"
              rel="noopener"
              @click.stop.prevent="openExplorerHome">https://explorer.natureserve.org/</a>
            </template>
          </i18n>
        </div>
      </div>
    </div>

    <Dialog
    v-if="isSustainabilityAdmin"
    :stateId="habitatFormDialogId"
    maxWidth="40em"
    @dialog-closing="closeHabitatForm"
    @close="closeHabitatForm">
      <MitigationHabitatForm
      :key="habitatSeed && habitatSeed.name"
      :propMitigationHabitat="habitatSeed"
      @mitigation-habitat-mutated="onHabitatCreated"/>
    </Dialog>
  </v-navigation-drawer>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { lookupSpeciesHabitat } from '@/utils/dueDiligence/natureServe.js'
import { taxonomyPresentation } from '@/utils/dueDiligence/taxonomyPresentation.js'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { UserClaims } from '@/utils/Enumerations.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { formatDate } from '@/utils/DateFormatter.js'

const REALM_LABEL_KEYS = {
  Terrestrial: 'habitatRealmTerrestrial',
  Palustrine: 'habitatRealmPalustrine',
  Riverine: 'habitatRealmRiverine',
  Lacustrine: 'habitatRealmLacustrine',
  Estuarine: 'habitatRealmEstuarine',
  Marine: 'habitatRealmMarine',
  Subterranean: 'habitatRealmSubterranean'
}

export default {
  name: 'NatureServeSlideOver',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    MitigationHabitatForm: () => import('@/components/settings-components/tract-settings/mitigation-habitat/MitigationHabitatForm.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    value: {
      type: Boolean,
      required: true
    },
    species: {
      type: Object,
      default: null
    },
    stateCode: {
      type: String,
      default: null
    }
  },

  data: () => ({
    loading: false,
    data: null,
    error: null,
    fetchSeq: 0,
    fetchKey: null,
    habitatSeed: undefined,
    habitatFormDialogId: uniqueDialogId('nature-serve-habitat-form')
  }),

  mounted () {
    // Needed to hoist the drawer element to the app root to avoid z-index issues with dialogs.
    const app = document.querySelector('[data-app]')
    if (app && this.$el) app.appendChild(this.$el)
    // Admins need the existing habitats to know which subtypes already have a corresponding MitigationHabitat.
    if (this.isSustainabilityAdmin && this.allMitigationHabitats.length === 0) this.fetchMitigationHabitats()
  },

  beforeDestroy () {
    // The hoisted element is no longer inside the parent subtree Vue tears down.
    if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },

  watch: {
    species: {
      immediate: true,
      handler (species) {
        if (species && this.value) this.fetch(species)
      }
    },

    value (isOpen) {
      if (isOpen && this.species) this.fetch(this.species)
      else if (!isOpen) this.closeHabitatForm()
    }
  },

  computed: {
    ...mapGetters('mitigation-habitat', ['allMitigationHabitats']),

    natureServeScientificName () {
      return this.data?.scientificName || null
    },

    natureServeCommonName () {
      return this.data?.commonName || null
    },

    headerPrimary () {
      return this.natureServeCommonName || this.natureServeScientificName
    },

    headerSecondary () {
      return this.natureServeCommonName ? this.natureServeScientificName : null
    },

    isClosestMatch () {
      return !!this.data?.match && !this.data.match.exact
    },

    closestMatchNotice () {
      const match = this.data?.match
      if (!match) return null
      return match.matchedName
        ? this.$t('closestMatchBody', { searched: match.searchedFor, matched: match.matchedName })
        : this.$t('closestMatchBodyUnnamed', { searched: match.searchedFor })
    },

    stateRankNotMatched () {
      return !!this.data && !!this.stateCode && !this.data.stateRank
    },

    recordDateLine () {
      if (!this.data) return null
      if (this.data.grankReviewDate) return this.$t('lastReviewed', { date: this.displayDate(this.data.grankReviewDate) })
      if (this.data.lastModified) return this.$t('recordLastModified', { date: this.displayDate(this.data.lastModified) })
      return null
    },

    header () {
      return taxonomyPresentation(this.data?.informalTaxonomy)
    },

    errorMessage () {
      if (this.error === 'timeout') return this.$t('natureServeTimeout')
      if (this.error === 'connection') return this.$t('natureServeConnectionError')
      return this.$t('natureServeServiceError')
    },

    isSustainabilityAdmin () {
      return userAssignedClaim(UserClaims.SustainabilityAdmin)
    },

    existingHabitatNames () {
      return new Set((this.allMitigationHabitats || []).map(h => (h.name || '').trim().toLowerCase()))
    },

    explorerUrl () {
      if (this.data?.url) return this.data.url
      return this.data?.elementId
        ? `https://explorer.natureserve.org/Taxon/${this.data.elementId}`
        : null
    },

    explorerLabel () {
      const explorer = this.$t('natureServeExplorer')
      return this.headerPrimary ? `${this.headerPrimary} | ${explorer}` : explorer
    },

    citationYear () {
      return new Date().getFullYear()
    },

    citationDate () {
      return formatDate(new Date(), 'MMMM D, YYYY')
    },

    rankTiles () {
      if (!this.data) return []
      const state = this.data.stateRank
      return [
        { label: this.$t('globalRank'), value: this.data.globalRank },
        { label: this.$t('roundedGlobalRank'), value: this.data.roundedGlobalRank },
        { label: this.stateRankLabel, value: state ? state.rank : null },
        { label: this.$t('esaStatus'), value: this.data.esaStatus }
      ]
    },

    stateRankLabel () {
      const state = this.data?.stateRank
      if (!state) return this.$t('stateRank')
      return state.rounded
        ? this.$t('roundedStateRankWithState', { state: state.subnationCode })
        : this.$t('stateRankWithState', { state: state.subnationCode })
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('mitigation-habitat', ['fetchMitigationHabitats']),

    habitatNameFor (realm, type) {
      return `${realm}: ${type}`
    },

    canCreateHabitat (realm, type) {
      return this.isSustainabilityAdmin &&
        !this.existingHabitatNames.has(this.habitatNameFor(realm, type).trim().toLowerCase())
    },

    startHabitatFromType (realm, type) {
      this.habitatSeed = { name: this.habitatNameFor(realm, type) }
      this.openOrUpdateDialog(this.habitatFormDialogId)
    },

    onHabitatCreated () {
      this.closeHabitatForm()
    },

    closeHabitatForm () {
      this.closeDialogsAtOrAbove(this.habitatFormDialogId)
      this.habitatSeed = undefined
    },

    async fetch (species) {
      const key = [species?.scientific, species?.common, this.stateCode].join('|')
      if (this.loading && this.fetchKey === key) return
      this.fetchKey = key
      const seq = ++this.fetchSeq
      this.loading = true
      this.data = null
      this.error = null
      try {
        const data = await lookupSpeciesHabitat(species, this.stateCode)
        if (seq !== this.fetchSeq) return
        this.data = data
        // Only match if the result is exact
        if (data && data.match?.exact) {
          this.$emit('resolved', { commonName: this.natureServeCommonName, scientificName: this.natureServeScientificName })
        }
      } catch (e) {
        if (seq !== this.fetchSeq) return
        console.error('NatureServe lookup failed', e)
        this.error = e?.code ?? 'service'
      } finally {
        if (seq === this.fetchSeq) this.loading = false
      }
    },

    openExplorer () {
      if (this.explorerUrl) window.open(this.explorerUrl, '_blank', 'noopener')
    },

    openExplorerHome () {
      window.open('https://explorer.natureserve.org/', '_blank', 'noopener')
    },

    realmLabel (realm) {
      const key = REALM_LABEL_KEYS[realm]
      return key ? this.$t(key) : realm
    },

    displayDate (value) {
      const parsed = moment(value)
      return parsed.isValid() ? parsed.format('MMMM D, YYYY') : value
    }
  }
}
</script>

<style scoped>
.nature-serve-slide-over ::v-deep .v-navigation-drawer__content {
  overflow: hidden;
}
.ns-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #FBF9F0;
}
.ns-header {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(135deg, #000000 0%, #0A0A0A 65%, #141414 100%);
  transition: background 200ms ease;
}
.ns-header-watermark {
  position: absolute;
  right: -18px;
  bottom: -26px;
  font-size: 130px !important;
  color: rgba(255, 255, 255, 0.06) !important;
  transform: rotate(-18deg);
}
.ns-header-badge {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.ns-eyebrow {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #A5D6A7;
}
.ns-common-name {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.25;
}
.ns-sci-name {
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}
.ns-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.ns-body::-webkit-scrollbar {
  width: 8px;
}
.ns-body::-webkit-scrollbar-thumb {
  background: rgba(27, 58, 42, 0.25);
  border-radius: 4px;
}
.ns-body::-webkit-scrollbar-thumb:hover {
  background: rgba(27, 58, 42, 0.4);
}
.ns-explorer-link {
  display: inline-flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: #2C5E40;
  text-decoration: none;
}
.ns-explorer-link:hover {
  text-decoration: underline;
}
.ns-rank-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.ns-rank-tile {
  background: #fff;
  border: 1px solid rgba(27, 58, 42, 0.12);
  border-left-width: 3px;
  border-left-color: rgba(27, 58, 42, 0.18);
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 1px 2px rgba(27, 58, 42, 0.06);
}
.ns-tile-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(27, 58, 42, 0.6);
}
.ns-tile-value {
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
}
.ns-tile-empty {
  color: rgba(27, 58, 42, 0.3) !important;
  font-weight: 400;
}
.ns-section-label {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #2C5E40;
  margin-bottom: 4px;
}
.ns-prose {
  font-size: 13.5px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.78);
}
.ns-habitat-group {
  margin-top: 10px;
  padding-left: 11px;
  border-left: 2px solid rgba(44, 94, 64, 0.22);
}
.ns-habitat-realm {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: rgba(27, 58, 42, 0.82);
  margin-bottom: 6px;
}
.ns-habitat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.ns-habitat-chip {
  border-color: rgba(44, 94, 64, 0.35) !important;
  color: #2C5E40 !important;
  background: rgba(143, 203, 155, 0.12) !important;
}
.ns-habitat-chip--actionable {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.ns-habitat-chip--actionable:hover {
  border-color: rgba(44, 94, 64, 0.7) !important;
  background: rgba(143, 203, 155, 0.32) !important;
}
.ns-habitat-chip-add {
  color: rgba(44, 94, 64, 0.55) !important;
}
.ns-habitat-chip--actionable:hover .ns-habitat-chip-add {
  color: #2C5E40 !important;
}
.ns-approx {
  background: #FDF6E3;
  border-left: 3px solid #C58A2E;
  border-radius: 4px;
  padding: 10px 12px;
}
.ns-approx-label {
  color: #8A5A1E;
}
.ns-threats {
  background: #FBEFE4;
  border-left: 3px solid var(--color-primary);
  border-radius: 4px;
  padding: 10px 12px;
}
.ns-threats-label {
  color: #B14A17;
}
.ns-degree-of-threat {
  color: #B14A17;
  margin-bottom: 6px;
}
.ns-muted {
  color: rgba(27, 58, 42, 0.55);
}
.ns-citation {
  font-size: 11px;
  line-height: 1.5;
  font-style: italic;
  color: rgba(27, 58, 42, 0.72);
}
.ns-citation a {
  color: #2C5E40;
  text-decoration: underline;
}
</style>
